import React, {
    Dispatch,
    FormEvent,
    JSXElementConstructor,
    ReactElement,
    SetStateAction,
    useEffect,
    useState
} from "react"
import Children from 'react-children-utilities'
import {classNames} from "@/library/utilities/html"
import {fieldDefinitions} from "@/components/forms/_form-helpers"
import FormInput, {FormInputProps} from "@/components/forms/form-input"
import {KeyValue} from "@/library/utilities/general"

type FormProps<T> = {
    defaultState: T
    children?: ReactElement | ReactElement[]
    onSubmit?: (event: FormEvent, data: T) => void
    onChange?: (data: T) => void
    passedState?: [T, Dispatch<SetStateAction<T>>]
    className?: string
}

export default function Form<T extends KeyValue<any>>(props: FormProps<T>) {
    const [formValues, setFormValues] = useState<T>(props.defaultState)
    const [passed, setPassed] = props.passedState ?? [undefined, undefined]

    const values: T = props.passedState == undefined || !passed
        ? formValues
        : passed as T
    const setValues: Dispatch<SetStateAction<T>> = props.passedState == undefined || !setPassed
        ? setFormValues
        : setPassed as Dispatch<SetStateAction<T>>

    useEffect(() => {
        props.onChange?.(values)
    }, [values, props])

    const inputChange = (field: string, value: any) => {
        const inputDefinitions = fieldDefinitions(field)

        setValues((current: T) => {
            const newValue = {...current}
            let v: any = newValue

            inputDefinitions.slice(0, inputDefinitions.length - 1).forEach(def => {
                v = def.index != undefined
                    ? v[def.name][def.index]
                    : v[def.name]
            })

            const last = inputDefinitions.last()
            if (last.index != undefined) {
                v[last.name][last.index] = value
            } else {
                v[last.name] = value
            }

            return newValue
        })
    }

    const onInputChange = (newValue: any, props: FormInputProps) => {
        //console.log(newValue, props)

        inputChange(props.name, newValue)
        props.onChange?.(newValue)
    }

    return <form
        className={classNames(props.className)}
        onSubmit={event => {
            event.preventDefault()
            return props.onSubmit?.(event, values)
        }}
    >
        {!values && <p>Laster...</p>}
        {values && Children.deepMap(props.children, (child, index) => {
            const element = child as ReactElement
            const type = element.type as JSXElementConstructor<any>

            //console.log(index, element)

            if (type?.name != 'FormInput')
                return child

            const inputDefinitions = fieldDefinitions(element.props.name)

            let validInput = true
            let v: any = values
            inputDefinitions.forEach(def => {
                if (!v) {
                    validInput = false
                    return
                }

                if (!Object.keys(v).includes(def.name)) {
                    validInput = false
                    return
                }

                v = def.index != undefined
                    ? v[def.name][def.index]
                    : v[def.name]
            })

            if (!validInput)
                return <div key={element.key} className="alert alert--error">
                    <p><code>{element.props.name}</code> er ikke et gyldig felt-navn.<br/>
                        En linje til med tekst for morroskyld.</p>
                </div>

            const finalValue: string | undefined = v

            return FormInput(element.props, `elementKey:${element.props.name}`, finalValue, onInputChange)
        })}
    </form>
}
